import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { CheckCircle, Heart, ChevronLeft, ChevronRight, Plus, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const today = new Date().toISOString().slice(0, 10);

const STEPS = [
  {
    key: 'step1', title: 'Child Information', desc: 'Tell us about your little one.',
    fields: [
      { name: 'child_first_name', label: 'Legal First Name', required: true },
      { name: 'child_middle_name', label: 'Middle Name' },
      { name: 'child_last_name', label: 'Legal Last Name', required: true },
      { name: 'child_preferred_name', label: 'Preferred Name' },
      { name: 'child_dob', label: 'Date of Birth', type: 'date', required: true },
      { name: 'child_gender', label: 'Gender', type: 'select', options: ['', 'Female', 'Male', 'Other', 'Prefer not to say'] },
      { name: 'primary_languages', label: 'Primary Language(s) Spoken at Home', required: true },
      { name: 'desired_start_date', label: 'Desired Start Date', type: 'date', required: true },
      { name: 'days_requested', label: 'Days Requested', type: 'days', required: true },
      { name: 'sibling_enrolled', label: 'A sibling is already enrolled at Maple Kids Academy', type: 'checkbox' },
      { name: 'immunization_confirmed', label: 'I confirm my child’s immunization records are up to date', type: 'checkbox' },
    ],
  },
  {
    key: 'step2', title: 'Primary Parent / Guardian', desc: 'Primary contact for your child.',
    fields: [
      { name: 'parent1_name', label: 'Full Name', required: true },
      { name: 'parent1_relationship', label: 'Relationship to Child', required: true },
      { name: 'parent1_phone', label: 'Phone Number', type: 'tel', required: true },
      { name: 'parent1_alt_phone', label: 'Alternate Phone', type: 'tel' },
      { name: 'parent1_email', label: 'Email Address', type: 'email', required: true },
      { name: 'parent1_address', label: 'Home Address', required: true },
      { name: 'parent1_employer', label: 'Employer' },
      { name: 'parent1_work_phone', label: 'Work Phone', type: 'tel' },
      { name: 'parent1_authorized', label: 'Authorized for pickup', type: 'checkbox' },
    ],
  },
  {
    key: 'step3', title: 'Secondary Parent / Guardian', desc: 'Optional — add a second guardian if applicable.',
    fields: [
      { name: 'parent2_name', label: 'Full Name' },
      { name: 'parent2_relationship', label: 'Relationship to Child' },
      { name: 'parent2_phone', label: 'Phone Number', type: 'tel' },
      { name: 'parent2_alt_phone', label: 'Alternate Phone', type: 'tel' },
      { name: 'parent2_email', label: 'Email Address', type: 'email' },
      { name: 'parent2_address', label: 'Home Address' },
      { name: 'parent2_employer', label: 'Employer' },
      { name: 'parent2_work_phone', label: 'Work Phone', type: 'tel' },
      { name: 'parent2_authorized', label: 'Authorized for pickup', type: 'checkbox' },
    ],
  },
  {
    key: 'step4', title: 'Authorized Pickups & Safety', desc: 'People allowed to pick up your child.',
    fields: [
      { name: 'safe_code', label: 'Family Safe Word / Pickup Code', required: true },
      { name: 'authorized_pickups', label: 'Authorized Pickups', type: 'pickups' },
    ],
  },
  {
    key: 'step5', title: 'Emergency Contacts', desc: 'Who we should call in an emergency.',
    fields: [
      { name: 'emergency_contacts', label: 'Emergency Contacts', type: 'contacts', required: true },
    ],
  },
  {
    key: 'step6', title: 'Health Information', desc: 'Medical details to keep your child safe.',
    fields: [
      { name: 'family_doctor', label: 'Family Doctor', required: true },
      { name: 'msp_number', label: 'MSP / Health Number' },
      { name: 'known_diagnoses', label: 'Known Diagnoses / Conditions', type: 'textarea' },
      { name: 'food_allergies', label: 'Food Allergies', type: 'textarea' },
      { name: 'medication_allergies', label: 'Medication Allergies', type: 'textarea' },
      { name: 'environmental_allergies', label: 'Environmental Allergies', type: 'textarea' },
      { name: 'allergy_severity', label: 'Allergy Severity', type: 'select', options: ['', 'Mild', 'Moderate', 'Severe', 'Life-threatening'] },
      { name: 'epipen_required', label: 'An EpiPen is required', type: 'checkbox' },
      { name: 'daycare_medications', label: 'Child takes medication during daycare hours', type: 'checkbox' },
      { name: 'medication_details', label: 'Medication Details', type: 'textarea' },
      { name: 'illness_policy_acknowledged', label: 'I have read and understand the illness policy', type: 'checkbox' },
    ],
  },
  {
    key: 'step7', title: 'Daily Care & Routine', desc: 'Help us keep your child comfortable.',
    fields: [
      { name: 'sleep_schedule', label: 'Sleep / Nap Schedule', type: 'textarea', required: true },
      { name: 'comfort_items', label: 'Comfort Items' },
      { name: 'feeding_method', label: 'Feeding Method', required: true },
      { name: 'milk_type', label: 'Milk Type' },
      { name: 'solids_started', label: 'Child has started solids', type: 'checkbox' },
      { name: 'food_restrictions', label: 'Food Restrictions', type: 'textarea' },
      { name: 'heating_instructions', label: 'Food Heating Instructions' },
      { name: 'diaper_brand', label: 'Diaper Brand' },
      { name: 'diaper_size', label: 'Diaper Size' },
      { name: 'cream_permission', label: 'Permission to apply diaper cream', type: 'checkbox' },
      { name: 'potty_readiness', label: 'Child is potty training', type: 'checkbox' },
      { name: 'potty_notes', label: 'Potty Training Notes', type: 'textarea' },
      { name: 'comforting_strategies', label: 'Comforting Strategies', type: 'textarea' },
    ],
  },
  {
    key: 'step8', title: 'Preferences & Permissions', desc: 'A few consents for daily activities.',
    fields: [
      { name: 'dietary_restrictions', label: 'Dietary Restrictions', type: 'textarea' },
      { name: 'cultural_preferences', label: 'Cultural / Religious Preferences', type: 'textarea' },
      { name: 'sunscreen_permission', label: 'Permission to apply sunscreen', type: 'checkbox' },
      { name: 'photo_consent', label: 'Consent to photograph my child for internal use', type: 'checkbox' },
      { name: 'walk_permission', label: 'Permission for neighbourhood walks', type: 'checkbox' },
    ],
  },
  {
    key: 'step9', title: 'Policies & Agreement', desc: 'Please review and acknowledge.',
    fields: [
      { name: 'policies_acknowledged', label: 'I have read and agree to Maple Kids Academy’s policies and procedures', type: 'checkbox', required: true },
      { name: 'parent_name_typed', label: 'Type Your Full Name', required: true },
      { name: 'acknowledgment_date', label: 'Date', type: 'date', required: true, default: today },
    ],
  },
  {
    key: 'step10', title: 'Signatures', desc: 'Sign to complete your application.',
    fields: [
      { name: 'parent1_signature', label: 'Primary Parent Signature (type full name)', required: true },
      { name: 'parent1_date', label: 'Date', type: 'date', required: true, default: today },
      { name: 'parent2_signature', label: 'Secondary Parent Signature (optional)' },
      { name: 'parent2_date', label: 'Date', type: 'date' },
    ],
  },
];

const buildInitial = () => {
  const d = {};
  STEPS.forEach((s) => {
    d[s.key] = {};
    s.fields.forEach((f) => {
      if (f.type === 'checkbox') d[s.key][f.name] = f.name === 'parent1_authorized';
      else if (f.type === 'days') d[s.key][f.name] = [];
      else if (f.type === 'pickups') d[s.key][f.name] = [];
      else if (f.type === 'contacts') d[s.key][f.name] = [{ name: '', relationship: '', phone: '', address: '' }];
      else d[s.key][f.name] = f.default || '';
    });
  });
  return d;
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const Enroll = () => {
  const [data, setData] = useState(buildInitial);
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/csrf-token`)
      .then((r) => setCsrfToken(r.data.csrf_token))
      .catch(() => {});
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const step = STEPS[current];
  const setField = (name, value) =>
    setData((d) => ({ ...d, [step.key]: { ...d[step.key], [name]: value } }));

  const stepValid = (s) =>
    s.fields.every((f) => {
      if (!f.required) return true;
      const v = data[s.key][f.name];
      if (f.type === 'days') return v.length > 0;
      if (f.type === 'checkbox') return v === true;
      if (f.type === 'contacts') return v.length > 0 && v.every((c) => c.name && c.relationship && c.phone && c.address);
      if (f.type === 'email') return emailOk(v);
      return String(v || '').trim() !== '';
    });

  const canProceed = stepValid(step) &&
    (step.key !== 'step2' || emailOk(data.step2.parent1_email));

  const next = () => { setError(''); if (current < STEPS.length - 1) { setCurrent(current + 1); scrollToTop(); } };
  const back = () => { setError(''); if (current > 0) { setCurrent(current - 1); scrollToTop(); } };

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      let token = csrfToken;
      if (!token) {
        try {
          token = (await axios.get(`${API}/csrf-token`)).data.csrf_token;
          setCsrfToken(token);
        } catch (err) {
          token = '';
        }
      }
      const payload = {
        ...data,
        csrf_token: token,
        step4: {
          ...data.step4,
          authorized_pickups: data.step4.authorized_pickups.filter(
            (p) => p.name && p.relationship && p.phone && p.id_type && p.id_number
          ),
        },
        step5: {
          ...data.step5,
          emergency_contacts: data.step5.emergency_contacts.filter(
            (c) => c.name && c.relationship && c.phone && c.address
          ),
        },
      };
      await axios.post(`${API}/enrollment/submit`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError('Something went wrong submitting your application. Please try again or call us at (604) 555-0100.');
    }
    setSubmitting(false);
  };

  const updatePickup = (i, key, value) =>
    setData((d) => {
      const arr = [...d.step4.authorized_pickups];
      arr[i] = { ...arr[i], [key]: value };
      return { ...d, step4: { ...d.step4, authorized_pickups: arr } };
    });
  const addPickup = () =>
    setData((d) => ({
      ...d,
      step4: { ...d.step4, authorized_pickups: [...d.step4.authorized_pickups, { name: '', relationship: '', phone: '', id_type: '', id_number: '' }] },
    }));
  const removePickup = (i) =>
    setData((d) => ({
      ...d,
      step4: { ...d.step4, authorized_pickups: d.step4.authorized_pickups.filter((_, idx) => idx !== i) },
    }));

  const updateContact = (i, key, value) =>
    setData((d) => {
      const arr = [...d.step5.emergency_contacts];
      arr[i] = { ...arr[i], [key]: value };
      return { ...d, step5: { ...d.step5, emergency_contacts: arr } };
    });
  const addContact = () =>
    setData((d) => ({
      ...d,
      step5: { ...d.step5, emergency_contacts: [...d.step5.emergency_contacts, { name: '', relationship: '', phone: '', address: '' }] },
    }));
  const removeContact = (i) =>
    setData((d) => ({
      ...d,
      step5: { ...d.step5, emergency_contacts: d.step5.emergency_contacts.filter((_, idx) => idx !== i) },
    }));

  const renderField = (f) => {
    const v = data[step.key][f.name];
    const id = `${step.key}-${f.name}`;
    if (f.type === 'checkbox') {
      return (
        <div key={f.name} className="flex items-start gap-3 py-2">
          <Checkbox id={id} checked={v} onCheckedChange={(c) => setField(f.name, c === true)} />
          <Label htmlFor={id} className="text-gray-700 leading-snug cursor-pointer">
            {f.label}{f.required ? ' *' : ''}
          </Label>
        </div>
      );
    }
    if (f.type === 'days') {
      return (
        <div key={f.name} className="md:col-span-2">
          <Label className="text-gray-700">{f.label}{f.required ? ' *' : ''}</Label>
          <div className="flex flex-wrap gap-3 mt-2">
            {DAYS.map((day) => (
              <label key={day} className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg cursor-pointer">
                <Checkbox
                  checked={v.includes(day)}
                  onCheckedChange={(c) =>
                    setField(f.name, c === true ? [...v, day] : v.filter((x) => x !== day))
                  }
                />
                <span className="text-sm text-gray-700">{day}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (f.type === 'pickups') {
      return (
        <div key={f.name} className="md:col-span-2">
          <Label className="text-gray-700">{f.label}</Label>
          <p className="text-sm text-gray-500 mb-3">Add anyone besides the guardians above who may pick up your child.</p>
          {data.step4.authorized_pickups.map((p, i) => (
            <Card key={i} className="mb-3 border-purple-100">
              <CardContent className="p-4 grid md:grid-cols-2 gap-3">
                <Input placeholder="Full Name" value={p.name} onChange={(e) => updatePickup(i, 'name', e.target.value)} />
                <Input placeholder="Relationship" value={p.relationship} onChange={(e) => updatePickup(i, 'relationship', e.target.value)} />
                <Input placeholder="Phone" value={p.phone} onChange={(e) => updatePickup(i, 'phone', e.target.value)} />
                <Input placeholder="ID Type (e.g. Driver's License)" value={p.id_type} onChange={(e) => updatePickup(i, 'id_type', e.target.value)} />
                <Input placeholder="ID Number" value={p.id_number} onChange={(e) => updatePickup(i, 'id_number', e.target.value)} />
                <Button type="button" variant="ghost" className="text-red-500 justify-start" onClick={() => removePickup(i)}>
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              </CardContent>
            </Card>
          ))}
          <Button type="button" variant="outline" onClick={addPickup} className="mt-1">
            <Plus className="h-4 w-4 mr-1" /> Add Authorized Pickup
          </Button>
        </div>
      );
    }
    if (f.type === 'contacts') {
      return (
        <div key={f.name} className="md:col-span-2">
          <Label className="text-gray-700">{f.label} *</Label>
          <p className="text-sm text-gray-500 mb-3">At least one emergency contact is required.</p>
          {data.step5.emergency_contacts.map((c, i) => (
            <Card key={i} className="mb-3 border-purple-100">
              <CardContent className="p-4 grid md:grid-cols-2 gap-3">
                <Input placeholder="Full Name" value={c.name} onChange={(e) => updateContact(i, 'name', e.target.value)} />
                <Input placeholder="Relationship" value={c.relationship} onChange={(e) => updateContact(i, 'relationship', e.target.value)} />
                <Input placeholder="Phone" value={c.phone} onChange={(e) => updateContact(i, 'phone', e.target.value)} />
                <Input placeholder="Address" value={c.address} onChange={(e) => updateContact(i, 'address', e.target.value)} />
                {data.step5.emergency_contacts.length > 1 && (
                  <Button type="button" variant="ghost" className="text-red-500 justify-start" onClick={() => removeContact(i)}>
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
          <Button type="button" variant="outline" onClick={addContact} className="mt-1">
            <Plus className="h-4 w-4 mr-1" /> Add Emergency Contact
          </Button>
        </div>
      );
    }
    if (f.type === 'textarea') {
      return (
        <div key={f.name} className="md:col-span-2">
          <Label htmlFor={id} className="text-gray-700">{f.label}{f.required ? ' *' : ''}</Label>
          <Textarea id={id} value={v} onChange={(e) => setField(f.name, e.target.value)} className="mt-1" rows={3} />
        </div>
      );
    }
    if (f.type === 'select') {
      return (
        <div key={f.name}>
          <Label htmlFor={id} className="text-gray-700">{f.label}{f.required ? ' *' : ''}</Label>
          <select
            id={id}
            value={v}
            onChange={(e) => setField(f.name, e.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {f.options.map((o) => (
              <option key={o} value={o}>{o === '' ? 'Select…' : o}</option>
            ))}
          </select>
        </div>
      );
    }
    const invalidEmail = f.type === 'email' && v && !emailOk(v);
    return (
      <div key={f.name}>
        <Label htmlFor={id} className="text-gray-700">{f.label}{f.required ? ' *' : ''}</Label>
        <Input
          id={id}
          type={f.type === 'tel' ? 'tel' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : 'text'}
          value={v}
          onChange={(e) => setField(f.name, e.target.value)}
          className={`mt-1 ${invalidEmail ? 'border-red-400' : ''}`}
        />
        {invalidEmail && <p className="text-xs text-red-500 mt-1">Please enter a valid email address.</p>}
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center py-20 px-4">
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50"></div>
        </div>
        <Card className="max-w-xl w-full shadow-xl border-2 border-green-200">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-3">Application Submitted!</h1>
            <p className="text-gray-600 leading-relaxed mb-6">
              Thank you for applying to Maple Kids Academy. We’ve received your enrollment application
              and our team will review it and reach out to you shortly with next steps.
            </p>
            <p className="text-sm text-gray-500">A confirmation has been sent to our admissions team. If you have questions, email <a href="mailto:info@mkids.ca" className="text-purple-600 font-semibold underline">info@mkids.ca</a>.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = ((current + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50"></div>
      </div>

      <section className="pt-16 pb-8 px-4 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
          <Heart className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">Enrollment Application</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          We’re now accepting applications! Complete the form below to enroll your child at Maple Kids Academy.
        </p>
      </section>

      <section className="pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Step {current + 1} of {STEPS.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <div className="relative">
          {submitting && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/75 backdrop-blur-sm rounded-xl">
              <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
              <p className="text-purple-700 font-semibold text-lg">Submitting your application…</p>
              <p className="text-gray-500 text-sm mt-1">Please wait a moment.</p>
            </div>
          )}
          <Card className="shadow-xl border-2 border-purple-100">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-1">{step.title}</h2>
              <p className="text-gray-500 mb-6">{step.desc}</p>

              <div className="grid md:grid-cols-2 gap-4">
                {step.fields.map(renderField)}
              </div>

              {error && <p className="text-red-500 text-sm mt-4">{error}</p>}

              <div className="flex justify-between items-center mt-8">
                <Button type="button" variant="outline" onClick={back} disabled={current === 0}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                {current < STEPS.length - 1 ? (
                  <Button
                    type="button"
                    onClick={next}
                    disabled={!canProceed}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={submit}
                    disabled={!canProceed || submitting}
                    className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold disabled:opacity-70"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                )}
              </div>
              {!canProceed && (
                <p className="text-xs text-gray-400 mt-3 text-right">Please complete the required fields (*) to continue.</p>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enroll;
